import { useMemo } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { MisServiceItem, MisDragItem } from './constructorTypes'

export function DragHandleIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'h-3 w-3'} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 6a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4zm8-16a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4z" />
    </svg>
  )
}

export function DraggableMisService({
  service,
  planId,
  misPlanUuid,
  selected,
  selectionCount,
  onToggleSelect,
  selectedItems,
  isUsed,
}: {
  service: MisServiceItem
  planId?: number | null | undefined
  misPlanUuid?: string | null
  selected: boolean
  selectionCount: number
  onToggleSelect: (event: React.MouseEvent | React.ChangeEvent) => void
  selectedItems: MisDragItem[]
  isUsed?: boolean
}) {
  const dragPayload = useMemo<MisDragItem[]>(() => {
    if (selected && selectionCount > 1) {
      return selectedItems
    }
    return [{ planId: planId ?? null, misPlanUuid: misPlanUuid ?? service.mis_plan_uuid ?? null, service }]
  }, [selected, selectionCount, selectedItems, planId, misPlanUuid, service])

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `mis-service:${service.id}`,
    data: {
      kind: 'mis-service',
      items: dragPayload,
    },
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  }
  const qty = service.quantity ?? 1
  const unitPrice = service.unit_price ?? service.total_price ?? 0
  const totalPrice = service.total_price ?? unitPrice * qty

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex w-full items-stretch border-b border-slate-100 text-[11px] text-slate-700 last:border-b-0 ${
        selected ? 'bg-blue-50/80' : isUsed ? 'bg-yellow-50 border-l-2 border-l-yellow-400 hover:bg-yellow-100/70' : 'bg-white hover:bg-slate-50'
      }`}
    >
      <label
        className="flex shrink-0 cursor-pointer items-center pl-1.5 pr-0.5"
        onClick={(event) => event.stopPropagation()}
      >
        <input
          type="checkbox"
          className="h-3 w-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-1 focus:ring-blue-400"
          checked={selected}
          onChange={(event) => {
            event.stopPropagation()
            onToggleSelect(event)
          }}
          onClick={(event) => event.stopPropagation()}
          title={selected ? 'Снять выделение' : 'Выделить услугу'}
        />
      </label>

      <div
        {...listeners}
        {...attributes}
        className="flex-1 cursor-grab px-1.5 py-1 active:cursor-grabbing"
        title={selected && selectionCount > 1
          ? `Перетащите выделенные услуги (${selectionCount}) в этап`
          : 'Перетащите услугу в этап конструктора'}
      >
        <div className="flex items-start gap-1.5">
          <span className="mt-0.5 text-slate-300 group-hover:text-slate-500">
            <DragHandleIcon />
          </span>
          <span
            className="flex-1 break-words font-medium leading-tight text-slate-800"
            style={{ wordBreak: 'break-word' }}
          >
            {service.service_name}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 pl-[18px] text-[10px] text-slate-500">
          <span>зуб: {service.tooth_number || '—'}</span>
          <span>×{qty}</span>
          <span>· {Number(unitPrice).toLocaleString('ru-RU')} ₽</span>
          <span className="ml-auto font-semibold text-slate-700">
            {Number(totalPrice).toLocaleString('ru-RU')} ₽
          </span>
        </div>
      </div>
    </div>
  )
}

export function DragOverlayCard({ items }: { items: MisDragItem[] }) {
  if (items.length === 0) return null
  if (items.length === 1) {
    const svc = items[0].service
    return (
      <div className="rounded-md border border-blue-400 bg-white px-2 py-1 text-[11px] text-slate-800 shadow-lg ring-2 ring-blue-200">
        <div className="font-medium leading-tight">{svc.service_name}</div>
        <div className="mt-0.5 text-[10px] text-slate-500">
          {svc.tooth_number ? `зуб ${svc.tooth_number} · ` : ''}×{svc.quantity ?? 1}
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-blue-400 bg-white px-2.5 py-1.5 text-[11px] text-slate-800 shadow-lg ring-2 ring-blue-200">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
        {items.length}
      </span>
      <span className="font-medium">Перенести услуги ({items.length})</span>
    </div>
  )
}
